import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authEvents } from '@/services/authEvents';
import { usePermissions } from './usePermissions';

interface UsePermissionGuardOptions {
  requiredPermission: () => boolean;
  redirectTo?: string;
  onPermissionLost?: () => void;
}

/**
 * Hook to guard components/pages based on permissions and handle permission changes
 */
export const usePermissionGuard = ({
  requiredPermission,
  redirectTo = '/unauthorized',
  onPermissionLost,
}: UsePermissionGuardOptions) => {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  // Check permission - wrapped in useCallback to fix ESLint warning
  const checkPermission = useCallback(() => {
    const allowed = requiredPermission();
    setHasPermission(allowed);
    setIsChecking(false);
    return allowed;
  }, [requiredPermission]);

  // Initial permission check
  useEffect(() => {
    checkPermission();
  }, [checkPermission, permissions]); // Re-check when permissions object changes

  // Listen for permission refresh events
  useEffect(() => {
    const handlePermissionRefresh = () => {
      // Re-check permission after refresh
      setTimeout(() => {
        const stillAllowed = checkPermission();

        if (!stillAllowed && hasPermission === true) {
          // User lost permission
          onPermissionLost?.();
          navigate(redirectTo, { replace: true });
        }
      }, 1500); // Wait a bit for permissions to update
    };

    authEvents.on('refreshPermissions', handlePermissionRefresh);

    return () => {
      authEvents.off('refreshPermissions', handlePermissionRefresh);
    };
  }, [hasPermission, navigate, redirectTo, onPermissionLost, checkPermission]);

  return {
    hasPermission: hasPermission === true,
    isChecking,
  };
};

/**
 * Hook for components that need to react to permission changes without redirecting
 */
export const useOnPermissionChange = (callback: () => void) => {
  useEffect(() => {
    authEvents.on('refreshPermissions', callback);

    return () => {
      authEvents.off('refreshPermissions', callback);
    };
  }, [callback]);
};
